import React, { useState, useEffect, useCallback, useRef } from 'react';
import ChatPanel from './ChatPanel';
import PreviewPanel from './PreviewPanel';
import ImportedProjectView from './ImportedProjectView';
import ScrapeLiveView from './ScrapeLiveView';
import BuildLanding from './build/BuildLanding';
import BuildTopHeader from './build/BuildTopHeader';
import { toast } from 'react-toastify';

const API = process.env.REACT_APP_BACKEND_URL || '';

// Replace a single ## section in markdown content
function replaceSectionInContent(markdown, heading, newContent) {
  const lines = markdown.split('\n');
  const clean = (s) => s.replace(/[^\w\s]/g, '').trim().toLowerCase();
  const target = clean(heading);
  let startIdx = -1;
  let endIdx = lines.length;

  for (let i = 0; i < lines.length; i++) {
    if (/^##\s/.test(lines[i])) {
      const lineClean = clean(lines[i].replace(/^##\s*/, ''));
      if (startIdx === -1 && lineClean.includes(target)) {
        startIdx = i;
      } else if (startIdx !== -1) {
        endIdx = i;
        break;
      }
    }
  }
  if (startIdx === -1) return markdown;
  return [...lines.slice(0, startIdx), newContent.trim(), ...lines.slice(endIdx)].join('\n');
}

export default function MainLayout({ user, setUser, initialChatId = null, initialProjectId = null, initialScrapeUrl = null, importedProject = null, onCloseImported, onActivateImported, onConsumeInitial, openTabs: openTabsProp, setOpenTabs: setOpenTabsProp }) {
  const [chats, setChats] = useState([]);
  const [activeChatId, setActiveChatId] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [previewBlocks, setPreviewBlocks] = useState([]);
  const [activeStage, setActiveStage] = useState('code');
  const [pipelineStage, setPipelineStage] = useState('code');
  const [lang, setLang] = useState('en');
  const [modifyMode, setModifyMode] = useState(false);
  const [selectedSection, setSelectedSection] = useState(null);
  const chatSendRef = useRef(null);
  const pendingFirstPromptRef = useRef(null);

  // Tabs currently open in the build top header. Each tab represents a
  // project the user is actively working on (chat + optional imported repo).
  // State is lifted to StudioLayout so tabs survive Build↔Projects navigation.
  // Falls back to local state if parent didn't pass it (e.g. tests).
  const [localOpenTabs, setLocalOpenTabs] = useState([]);
  const openTabs = openTabsProp ?? localOpenTabs;
  const setOpenTabs = setOpenTabsProp ?? setLocalOpenTabs;

  // Agent roster (active deployed agents for this user) + current selection
  const [agentRoster, setAgentRoster] = useState([]);
  const [activeAgent, setActiveAgent] = useState({
    agent_id: 'borntobealive-ai', name: 'Aria', color: '#7C3AED',
  });
  // Scrape session — when Web Intelligence triggered
  const [scrapeSession, setScrapeSession] = useState(null); // { url }
  // Tracks when the AI is actively streaming a response — used to block double NEXT clicks
  const [chatStreaming, setChatStreaming] = useState(false);

  // Load active agents roster for the switcher
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${API}/api/agents`, { credentials: 'include' });
        if (r.ok && !cancelled) {
          const data = await r.json();
          const list = (data || []).filter(a => a.status === 'active');
          // Ensure core agent always present
          if (!list.some(a => a.agent_id === 'borntobealive-ai')) {
            list.unshift({ agent_id: 'borntobealive-ai', name: 'Aria', color: '#7C3AED', role: 'Builder & Designer' });
          }
          setAgentRoster(list);
        }
      } catch {}
    })();
    return () => { cancelled = true; };
  }, []);

  // Handle section click — just select it, don't send anything
  const handleSectionSelect = useCallback((sectionText) => {
    setSelectedSection(sectionText);
    setModifyMode(false);
  }, []);

  // Handle next stage from pipeline bar (legacy — kept for compatibility, no-op in single-shot)
  const handleNextStage = useCallback(() => {}, []);

  // Surgically replace ONE section in the latest block
  const handleApplySection = useCallback((sectionHeading, newSectionMarkdown) => {
    setPreviewBlocks(prev => {
      const updated = [...prev];
      for (let i = updated.length - 1; i >= 0; i--) {
        if (updated[i].stage === pipelineStage && updated[i].type === 'content') {
          const old = updated[i].content;
          const replaced = replaceSectionInContent(old, sectionHeading, newSectionMarkdown);
          if (replaced !== old) {
            updated[i] = { ...updated[i], content: replaced };
          }
          break;
        }
      }
      return updated;
    });
    setSelectedSection(null);
  }, [pipelineStage]);

  // Handle template selection from inspiration cards
  const handleSelectTemplate = useCallback(async (card) => {
    try {
      // 1. Fetch the template source code from backend
      const res = await fetch(`${API}/api/fetch-template`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ registry_id: card.registryId }),
      });
      if (!res.ok) throw new Error('Failed to fetch template');
      const template = await res.json();

      // 2. Build a prompt that sends the code to Claude for transformation
      const prompt = `I selected the "${card.name}" component as design inspiration. Here is the original source code (TypeScript/React):

\`\`\`tsx
${template.code}
\`\`\`

Transform this into a complete, standalone HTML page using Tailwind CSS CDN. Requirements:
- Convert TypeScript to vanilla HTML/CSS/JS
- Remove all Next.js, shadcn, and external dependencies
- Keep the same visual design and animations (use CSS animations instead of framer-motion)
- Make it production-ready and beautiful
- Adapt it to my project context
- Output ONLY the complete HTML code block`;

      // 3. Send the prompt through the chat
      if (chatSendRef.current) {
        chatSendRef.current(prompt);
      }
      toast.success(`Template "${card.name}" selected`);
    } catch (err) {
      console.error('Template fetch error:', err);
      toast.error('Failed to load template');
    }
  }, []);

  const loadChats = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/chats`, { credentials: 'include' });
      if (res.ok) setChats(await res.json());
    } catch (err) { console.error('Failed to load chats:', err); }
  }, []);

  useEffect(() => { loadChats(); }, [loadChats]);

  // If a project / chat was opened from the Workspace, select it once chats load
  useEffect(() => {
    if (!initialChatId) return;
    setActiveChatId(initialChatId);
    setPreviewBlocks([]);
    setActiveStage('code');
    setPipelineStage('code');
    setSidebarOpen(false);
    if (onConsumeInitial) onConsumeInitial();
  }, [initialChatId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Safety net: whenever an imported project is opened in Build, make sure
  // the active chat is LINKED to that project_id. Otherwise the backend has
  // no idea this chat is about a repo, and Aria fails to read code/preview.
  useEffect(() => {
    let cancelled = false;
    if (!importedProject?.project_id) return;
    (async () => {
      try {
        // 1) If we have an active chat, check if it's already linked
        if (activeChatId) {
          const r = await fetch(`${API}/api/chats/${activeChatId}`, { credentials: 'include' });
          if (r.ok) {
            const chat = await r.json();
            if (chat.project_id === importedProject.project_id) return;
            // Patch the chat to link it to the project
            await fetch(`${API}/api/chats/${activeChatId}`, {
              method: 'PATCH', credentials: 'include',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ project_id: importedProject.project_id }),
            });
            return;
          }
        }
        // 2) No active chat → look for an existing chat linked to this project
        const detRes = await fetch(`${API}/api/projects/${importedProject.project_id}/details`, { credentials: 'include' });
        if (detRes.ok) {
          const det = await detRes.json();
          if (Array.isArray(det.sessions) && det.sessions.length > 0 && !cancelled) {
            setActiveChatId(det.sessions[0].chat_id);
            return;
          }
        }
        // 3) Create a fresh chat linked to this project
        const cRes = await fetch(`${API}/api/chats`, {
          method: 'POST', credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            title: importedProject.full_name || importedProject.name || 'Imported repo',
            project_id: importedProject.project_id,
          }),
        });
        if (cRes.ok && !cancelled) {
          const chat = await cRes.json();
          setChats(prev => [chat, ...prev.filter(c => c.chat_id !== chat.chat_id)]);
          setActiveChatId(chat.chat_id);
        }
      } catch {}
    })();
    return () => { cancelled = true; };
  }, [importedProject?.project_id, activeChatId]);

  // If asked to re-scrape a URL → switch to Web Intelligence agent and trigger
  useEffect(() => {
    if (!initialScrapeUrl) return;
    setActiveAgent({ agent_id: 'web-intelligence', name: 'Kai', color: '#06B6D4' });
    setScrapeSession({ url: initialScrapeUrl });
    if (onConsumeInitial) onConsumeInitial();
  }, [initialScrapeUrl]); // eslint-disable-line react-hooks/exhaustive-deps

  const createNewChat = async () => {
    try {
      const res = await fetch(`${API}/api/chats`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ title: 'New Chat' }),
      });
      if (res.ok) {
        const chat = await res.json();
        setChats(prev => [chat, ...prev]);
        setActiveChatId(chat.chat_id);
        setPreviewBlocks([]);
        setActiveStage('code');
        setPipelineStage('code');
        setSidebarOpen(false);
        return chat.chat_id;
      }
    } catch (err) { toast.error('Failed to create chat'); }
    return null;
  };

  const deleteChat = async (chatId) => {
    try {
      const res = await fetch(`${API}/api/chats/${chatId}`, { method: 'DELETE', credentials: 'include' });
      if (res.ok) {
        setChats(prev => prev.filter(c => c.chat_id !== chatId));
        if (activeChatId === chatId) {
          setActiveChatId(null);
          setPreviewBlocks([]);
          setActiveStage('code');
          setPipelineStage('code');
        }
        toast.success('Chat deleted');
      }
    } catch (err) { toast.error('Failed to delete chat'); }
  };

  const handleLogout = async () => {
    try { await fetch(`${API}/api/auth/logout`, { method: 'POST', credentials: 'include' }); } catch {}
    setUser(null);
  };

  // When a new chat is selected, reset preview then load existing messages
  const handleSelectChat = (id) => {
    setActiveChatId(id);
    setPreviewBlocks([]);
    setActiveStage('code');
    setPipelineStage('code');
    setSidebarOpen(false);
  };

  // ===== Build top header — open tabs management =====
  // Sync openTabs whenever activeChatId or importedProject changes.
  useEffect(() => {
    if (!activeChatId && !importedProject) return;
    const id = importedProject?.project_id ? `proj:${importedProject.project_id}` : `chat:${activeChatId}`;
    const title = importedProject?.full_name
      || importedProject?.name
      || chats.find(c => c.chat_id === activeChatId)?.title
      || 'New project';
    setOpenTabs(prev => {
      if (prev.some(t => t.id === id)) {
        // Refresh title in case it changed
        return prev.map(t => t.id === id ? { ...t, title } : t);
      }
      return [...prev, {
        id,
        title,
        chat_id: activeChatId,
        imported: importedProject || null,
      }];
    });
  }, [activeChatId, importedProject, chats]);

  const activeTabId = importedProject?.project_id
    ? `proj:${importedProject.project_id}`
    : (activeChatId ? `chat:${activeChatId}` : null);

  const handleSelectTab = useCallback((tab) => {
    // If this tab is for an imported project, ask the parent to re-mount it.
    // Imported project state lives in StudioLayout, so we need a callback.
    if (tab.imported) {
      if (onActivateImported) onActivateImported(tab.imported, tab.chat_id);
      else if (tab.chat_id) handleSelectChat(tab.chat_id);
      return;
    }
    // Pure generative build → also clear any mounted imported project so
    // we don't show the wrong preview, then load the chat.
    if (importedProject && onCloseImported) onCloseImported();
    if (tab.chat_id) handleSelectChat(tab.chat_id);
  }, [onActivateImported, onCloseImported, importedProject]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCloseTab = useCallback((tab) => {
    setOpenTabs(prev => {
      const next = prev.filter(t => t.id !== tab.id);
      // If we just closed the active tab, jump to the next one (or landing).
      const wasActive = tab.id === activeTabId;
      if (wasActive) {
        const fallback = next[next.length - 1];
        if (fallback?.imported && onCloseImported) onCloseImported();
        if (fallback?.chat_id) {
          handleSelectChat(fallback.chat_id);
        } else {
          // No tabs left → reset to BuildLanding
          if (tab.imported && onCloseImported) onCloseImported();
          setActiveChatId(null);
          setPreviewBlocks([]);
          setActiveStage('code');
          setPipelineStage('code');
        }
      } else if (tab.imported && onCloseImported && importedProject?.project_id === tab.imported.project_id) {
        // closing an imported tab that's mounted but not the focused one
        onCloseImported();
      }
      return next;
    });
  }, [activeTabId, onCloseImported, importedProject]); // eslint-disable-line react-hooks/exhaustive-deps

  // "+" → fresh BuildLanding (does NOT create an empty chat)
  const handleNewProject = useCallback(() => {
    if (importedProject && onCloseImported) onCloseImported();
    setActiveChatId(null);
    setPreviewBlocks([]);
    setSelectedSection(null);
    setActiveStage('code');
    setPipelineStage('code');
    setScrapeSession(null);
  }, [importedProject, onCloseImported]);

  // Same as "+" but exposed semantically as "Home" pill
  const handleGoHome = handleNewProject;

  // Parse AI response — single-shot, always lands on the code stage.
  const handleAIResponse = useCallback((content) => {
    if (!content || content.length < 10) return;

    // Strip any legacy stage markers (Aria might still emit them for safety).
    let cleanContent = content
      .replace(/\[STAGE:wireframe\]/g, '')
      .replace(/\[STAGE:design\]/g, '')
      .replace(/\[STAGE:code\]/g, '')
      .trim();

    // Extract code from triple-backtick fences
    const codePattern = /```(\w*)\n([\s\S]*?)```/g;
    let match;
    const codeBlocks = [];
    let idx = 0;
    const timestamp = Date.now();

    while ((match = codePattern.exec(cleanContent)) !== null) {
      const lang = (match[1] || '').toLowerCase();
      const code = match[2].trim();
      // EVERY fenced block becomes a code block — the WebContainer needs the
      // full project (package.json, vite.config.js, CSS, etc.) to boot.
      // The parser downstream decides whether to mount a multi-file project
      // or render a single HTML page.
      if (code.length > 20) {
        codeBlocks.push({ type: 'code', stage: 'code', id: `code_${idx}_${timestamp}`, language: lang || 'text', content: code, title: `Code ${idx + 1}` });
        idx++;
      }
    }

    // Detect inline HTML (no backtick fences) — AI sometimes sends raw HTML directly
    if (codeBlocks.length === 0) {
      const hasInlineHtml = cleanContent.includes('<!DOCTYPE') || (cleanContent.includes('<html') && cleanContent.includes('</html>')) || (cleanContent.includes('<head') && cleanContent.includes('<body'));
      if (hasInlineHtml) {
        let start = cleanContent.indexOf('<!DOCTYPE');
        if (start === -1) start = cleanContent.indexOf('<html');
        if (start === -1) start = cleanContent.indexOf('<head');
        if (start >= 0) {
          const end = cleanContent.lastIndexOf('</html>');
          const htmlCode = end > start ? cleanContent.slice(start, end + 7) : cleanContent.slice(start);
          if (htmlCode.length > 100) {
            codeBlocks.push({ type: 'code', stage: 'code', id: `code_inline_${timestamp}`, language: 'html', content: htmlCode, title: 'Code' });
          }
        }
      }
    }

    const hasCode = codeBlocks.length > 0;
    const newBlocks = [];
    if (hasCode) newBlocks.push(...codeBlocks);
    if (cleanContent.length > 30) {
      newBlocks.push({ type: 'content', stage: 'code', id: `code_${timestamp}_${Math.random().toString(36).slice(2, 6)}`, content: cleanContent, title: 'Code' });
    }

    if (newBlocks.length > 0) {
      setPreviewBlocks(prev => {
        const updated = [...prev];
        for (const block of newBlocks) {
          if (block.type === 'content') {
            const existingIdx = updated.findIndex(b => b.type === 'content' && b.stage === block.stage);
            if (existingIdx !== -1) {
              updated[existingIdx] = {
                ...updated[existingIdx],
                content: updated[existingIdx].content + '\n\n---\n\n' + block.content,
              };
            } else {
              updated.push(block);
            }
          } else {
            updated.push(block);
          }
        }
        return updated;
      });
      setActiveStage('code');
      setPipelineStage('code');
    }
  }, []);

  // Trigger a scrape session (called from ChatPanel when Web Intelligence + URL detected)
  const handleScrapeTrigger = useCallback((url) => {
    setScrapeSession({ url });
  }, []);

  const closeScrape = useCallback(() => setScrapeSession(null), []);

  // Auto-leave scrape mode if user switches back to a build agent
  useEffect(() => {
    if (activeAgent?.agent_id !== 'web-intelligence' && scrapeSession) {
      setScrapeSession(null);
    }
  }, [activeAgent, scrapeSession]);

  const isWebIntel = activeAgent?.agent_id === 'web-intelligence';

  // ===== Single-shot Build Landing =====
  // Submit a prompt from the BuildLanding (input bubble or template card).
  // Creates a fresh chat then fires the first message as soon as ChatPanel mounts.
  const handleLandingSubmit = useCallback(async (prompt) => {
    if (!prompt || !prompt.trim()) return;
    pendingFirstPromptRef.current = prompt.trim();
    const id = await createNewChat();
    if (!id) {
      pendingFirstPromptRef.current = null;
      return;
    }
    // Poll for ChatPanel's send handler — it gets wired on mount.
    const start = Date.now();
    const tryFire = () => {
      if (!pendingFirstPromptRef.current) return;
      if (chatSendRef.current) {
        const msg = pendingFirstPromptRef.current;
        pendingFirstPromptRef.current = null;
        chatSendRef.current(msg);
        return;
      }
      if (Date.now() - start > 4000) {
        pendingFirstPromptRef.current = null;
        return;
      }
      setTimeout(tryFire, 60);
    };
    setTimeout(tryFire, 60);
  }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  // Show the BuildLanding when nothing is active in the workspace.
  const showLanding = (
    !activeChatId
    && !importedProject
    && !scrapeSession
    && !isWebIntel
  );

  return (
    <div data-testid="main-layout" style={{
      display: 'flex', flexDirection: 'column',
      height: '100%', background: '#0F0F15', overflow: 'hidden',
    }}>
      <BuildTopHeader
        tabs={openTabs}
        activeTabId={activeTabId}
        onSelectTab={handleSelectTab}
        onCloseTab={handleCloseTab}
        onNewProject={handleNewProject}
        onGoHome={handleGoHome}
        user={user}
        credits={user?.credits ?? null}
        notifCount={0}
        variant={showLanding ? 'light' : 'dark'}
      />
      <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        {showLanding ? (
          <div style={{ flex: 1, display: 'flex', overflow: 'hidden', minWidth: 0 }}>
            <BuildLanding
              onSubmit={handleLandingSubmit}
              recentChats={chats}
              onSelectChat={handleSelectChat}
            />
          </div>
        ) : scrapeSession ? (
          <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
            <ScrapeLiveView
              url={scrapeSession.url}
              onClose={closeScrape}
            />
          </div>
        ) : importedProject ? (
          <div style={{ flex: 1, display: 'flex', overflow: 'hidden', minWidth: 0 }}>
            <ImportedProjectView
              project={importedProject}
              onClose={onCloseImported}
              embedded={true}
              chatId={activeChatId}
            />
          </div>
        ) : isWebIntel ? (
          <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
            <WebIntelEmptyState onStart={handleScrapeTrigger} />
          </div>
        ) : (
          <PreviewPanel
            blocks={previewBlocks}
            activeStage={activeStage}
            setActiveStage={setActiveStage}
            chatStarted={!!activeChatId}
            pipelineStage={pipelineStage}
            onSelectTemplate={handleSelectTemplate}
            modifyMode={modifyMode}
            onSectionSelect={handleSectionSelect}
            onNextStage={handleNextStage}
            nextStageDisabled={chatStreaming}
            lang={lang}
            onClose={handleNewProject}
            chatId={activeChatId}
          />
        )}
        {!showLanding && (
          <>
            <div style={{ width: 1, background: 'rgba(255,255,255,0.06)', flexShrink: 0 }} />
            <ChatPanel
              activeChatId={activeChatId}
              onNewChat={createNewChat}
              onChatCreated={loadChats}
              user={user}
              onAIResponse={handleAIResponse}
              sendRef={chatSendRef}
              onLangDetected={setLang}
              lang={lang}
              pipelineStage={pipelineStage}
              modifyMode={modifyMode}
              onModifyMode={setModifyMode}
              selectedSection={selectedSection}
              onClearSection={() => setSelectedSection(null)}
              onApplySection={handleApplySection}
              agentRoster={agentRoster}
              activeAgent={activeAgent}
              onSwitchAgent={setActiveAgent}
              onScrapeTrigger={handleScrapeTrigger}
              onStreamingChange={setChatStreaming}
            />
          </>
        )}
      </div>
    </div>
  );
}

/* ========== Web Intelligence — entry state on the left ========== */
function WebIntelEmptyState({ onStart }) {
  const [url, setUrl] = useState('');
  const submit = (e) => {
    e.preventDefault();
    const u = url.trim();
    if (!u) return;
    onStart(u);
  };
  const examples = ['https://vercel.com', 'https://stripe.com', 'https://linear.app'];
  return (
    <div data-testid="web-intel-entry" style={{
      flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: 'radial-gradient(ellipse at center, #0F0F15 0%, #06060A 100%)',
      position: 'relative', overflow: 'hidden', padding: 24,
    }}>
      <div style={{
        position: 'absolute', inset: 0, pointerEvents: 'none',
        backgroundImage: `
          linear-gradient(rgba(6,182,212,0.04) 1px, transparent 1px),
          linear-gradient(90deg, rgba(6,182,212,0.04) 1px, transparent 1px)`,
        backgroundSize: '42px 42px', opacity: 0.55,
        animation: 'grid-drift 26s linear infinite',
      }} />
      <div style={{
        position: 'absolute', top: '50%', left: '50%',
        width: 560, height: 560, marginLeft: -280, marginTop: -280,
        background: 'radial-gradient(circle, rgba(6,182,212,0.14) 0%, transparent 65%)',
        pointerEvents: 'none',
      }} />

      <form onSubmit={submit} style={{
        position: 'relative', zIndex: 2, width: '100%', maxWidth: 540,
      }}>
        <div style={{
          display: 'inline-flex', alignItems: 'center', gap: 8,
          padding: '5px 12px', marginBottom: 22,
          background: 'rgba(6,182,212,0.1)',
          border: '1px solid rgba(6,182,212,0.25)',
          fontSize: 10, fontWeight: 700, color: '#06B6D4',
          letterSpacing: '0.12em', textTransform: 'uppercase',
        }}>
          <span style={{
            width: 6, height: 6, borderRadius: '50%', background: '#06B6D4',
            boxShadow: '0 0 8px #06B6D4', animation: 'pulse-dot 1.2s ease-in-out infinite',
          }} />
          Kai · Scraping Expert
        </div>
        <h2 style={{
          fontSize: 36, fontWeight: 800, color: '#fff',
          margin: '0 0 10px', letterSpacing: '-0.02em', lineHeight: 1.1,
        }}>
          Scrape any site,<br />extract everything.
        </h2>
        <p style={{ fontSize: 14, color: 'rgba(255,255,255,0.55)', margin: '0 0 28px', lineHeight: 1.6 }}>
          Paste a URL below. I'll capture images, map links, detect the tech stack, and surface every insight in real time — with structured data ready to export.
        </p>

        <div style={{
          display: 'flex', gap: 0,
          border: '1px solid rgba(6,182,212,0.3)',
          background: 'rgba(255,255,255,0.02)',
          transition: 'all 0.2s',
        }}>
          <input
            autoFocus
            type="text"
            value={url}
            onChange={e => setUrl(e.target.value)}
            placeholder="https://example.com"
            data-testid="web-intel-url-input"
            style={{
              flex: 1, padding: '16px 18px',
              background: 'transparent', border: 'none', outline: 'none',
              color: '#fff', fontSize: 15, fontFamily: "'JetBrains Mono', monospace",
            }}
          />
          <button
            type="submit"
            disabled={!url.trim()}
            data-testid="web-intel-start-btn"
            style={{
              padding: '0 26px',
              background: url.trim() ? 'linear-gradient(135deg, #06B6D4, #22D3EE)' : 'rgba(255,255,255,0.04)',
              color: url.trim() ? '#000' : 'rgba(255,255,255,0.3)',
              border: 'none', fontSize: 13, fontWeight: 700, fontFamily: 'inherit',
              cursor: url.trim() ? 'pointer' : 'not-allowed',
              letterSpacing: '0.03em',
              transition: 'all 0.2s',
            }}
            onMouseEnter={e => { if (url.trim()) e.currentTarget.style.boxShadow = '0 0 28px rgba(6,182,212,0.5)'; }}
            onMouseLeave={e => e.currentTarget.style.boxShadow = 'none'}
          >
            Scrape →
          </button>
        </div>

        <div style={{ marginTop: 22, display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.35)', letterSpacing: '0.1em', textTransform: 'uppercase' }}>
            Try
          </span>
          {examples.map(ex => (
            <button
              key={ex}
              type="button"
              onClick={() => setUrl(ex)}
              style={{
                padding: '5px 10px', background: 'rgba(255,255,255,0.04)',
                border: '1px solid rgba(255,255,255,0.08)', fontSize: 11,
                color: 'rgba(255,255,255,0.6)', cursor: 'pointer', fontFamily: 'inherit',
                transition: 'all 0.15s',
              }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = 'rgba(6,182,212,0.3)'; e.currentTarget.style.color = '#06B6D4'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.08)'; e.currentTarget.style.color = 'rgba(255,255,255,0.6)'; }}
            >
              {ex.replace('https://', '')}
            </button>
          ))}
        </div>

        <div style={{
          marginTop: 34, display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10,
        }}>
          {[
            { label: 'Full-page scraping', hint: 'DOM, text, structure' },
            { label: 'Image & media capture', hint: 'All assets extracted' },
            { label: 'Tech stack detection', hint: 'Frameworks & scripts' },
          ].map(f => (
            <div key={f.label} style={{
              padding: 12, background: 'rgba(255,255,255,0.02)',
              border: '1px solid rgba(255,255,255,0.05)',
            }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#fff', marginBottom: 3 }}>{f.label}</div>
              <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)' }}>{f.hint}</div>
            </div>
          ))}
        </div>
      </form>

      <style>{`
        @keyframes grid-drift { from { background-position: 0 0; } to { background-position: 42px 42px; } }
        @keyframes pulse-dot { 0%,100% { opacity: 1; } 50% { opacity: 0.4; } }
      `}</style>
    </div>
  );
}
